import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";
import {
    Users,
    BookOpen,
    TrendingUp,
    Activity,
    Trophy,
    Target,
    UserCog,
    Heart,
    ChevronRight,
    CheckCircle2,
    XCircle,
    Eye,
} from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";

export default function Equipe() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [mastery, setMastery] = useState(null);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/team/stats"),
            api.get("/team/users"),
            api.get("/team/social-mastery"),
        ]).then(([s, u, m]) => {
            setStats(s.data);
            setUsers(u.data);
            setMastery(m.data);
        });
    }, []);

    if (!stats) {
        return (
            <Layout title="Équipe" subtitle="Pilotage des performances">
                <div className="h-64 glass-card rounded-3xl animate-pulse" />
            </Layout>
        );
    }

    return (
        <Layout title="Équipe" subtitle="Pilotage des performances">
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
                {[
                    { label: "Apprenants actifs", value: stats.active_users, icon: Users, color: "#EE722E" },
                    { label: "Modules disponibles", value: stats.total_modules, icon: BookOpen, color: "#FCDB53" },
                    { label: "Taux de réussite", value: `${stats.success_rate}%`, icon: TrendingUp, color: "#F89F63" },
                    { label: "Quiz tentés", value: stats.total_attempts, icon: Target, color: "#EE722E" },
                ].map((s, i) => (
                    <motion.div
                        key={s.label}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="col-span-6 lg:col-span-3 glass-card rounded-3xl p-5 lg:p-6"
                        data-testid={`equipe-stat-${i}`}
                    >
                        <div
                            className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3"
                            style={{ background: `${s.color}25` }}
                        >
                            <s.icon className="w-5 h-5" color={s.color} />
                        </div>
                        <p className="text-3xl lg:text-4xl font-extrabold text-white mb-1">
                            {s.value}
                        </p>
                        <p className="text-xs font-thin tracking-[0.2em] uppercase text-white/40">
                            {s.label}
                        </p>
                    </motion.div>
                ))}

                {/* Activity */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="col-span-12 lg:col-span-7 glass-card rounded-3xl p-6 lg:p-7"
                    data-testid="equipe-activity"
                >
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                                Activité plateforme
                            </p>
                            <h3 className="text-xl font-extrabold text-white">7 derniers jours</h3>
                        </div>
                        <Activity className="w-6 h-6 text-shariti-orange" />
                    </div>
                    <div className="h-52 -mx-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.activity_7d}>
                                <XAxis
                                    dataKey="day"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: "#888", fontSize: 11 }}
                                />
                                <Tooltip
                                    cursor={{ fill: "rgba(238,114,46,0.05)" }}
                                    contentStyle={{
                                        background: "#181818",
                                        border: "1px solid #EE722E",
                                        borderRadius: 12,
                                        fontFamily: "Montserrat",
                                    }}
                                    labelStyle={{ color: "#EE722E", fontWeight: 800, marginBottom: 4 }}
                                    itemStyle={{ color: "#fff", fontWeight: 700 }}
                                    formatter={(value) => [`${value} quiz`, null]}
                                    separator=""
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]}>
                                    {stats.activity_7d.map((_, i) => (
                                        <Cell
                                            key={i}
                                            fill={
                                                i === stats.activity_7d.length - 1
                                                    ? "#FCDB53"
                                                    : "#EE722E"
                                            }
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>

                {/* Top performers */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="col-span-12 lg:col-span-5 glass-card rounded-3xl p-6 lg:p-7"
                    data-testid="equipe-top-performers"
                >
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                                Top 10
                            </p>
                            <h3 className="text-xl font-extrabold text-white">Performers</h3>
                        </div>
                        <Trophy className="w-6 h-6 text-shariti-yellow" />
                    </div>
                    <div className="space-y-2">
                        {stats.top_users.map((u, i) => (
                            <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-xl">
                                <span
                                    className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold ${
                                        i === 0
                                            ? "bg-shariti-orange text-white"
                                            : i === 1
                                            ? "bg-shariti-yellow text-shariti-black"
                                            : i === 2
                                            ? "bg-shariti-orange-light text-shariti-black"
                                            : "bg-white/5 text-white/60"
                                    }`}
                                >
                                    {i + 1}
                                </span>
                                {u.avatar ? (
                                    <img
                                        src={u.avatar}
                                        alt=""
                                        className="w-8 h-8 rounded-lg object-cover"
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded-lg bg-shariti-orange/20 flex items-center justify-center text-sm font-bold text-shariti-orange">
                                        {u.name?.[0]}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="truncate text-sm font-bold text-white">
                                        {u.name}
                                    </p>
                                    <p className="text-[10px] text-white/40 capitalize">
                                        {u.role}
                                    </p>
                                </div>
                                <p className="text-sm font-extrabold text-shariti-orange">
                                    {u.points}
                                </p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Detailed learner table */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="col-span-12 glass-card rounded-3xl overflow-hidden"
                    data-testid="equipe-users-table"
                >
                    <div className="p-5 lg:p-6 border-b border-white/5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                                    Suivi détaillé
                                </p>
                                <h3 className="text-xl font-extrabold text-white">Mes apprenants</h3>
                            </div>
                            <UserCog className="w-6 h-6 text-shariti-orange-light" />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-white/3">
                                <tr className="text-left text-xs font-thin tracking-widest text-white/50 uppercase">
                                    <th className="p-4">Apprenant</th>
                                    <th className="p-4 hidden md:table-cell">Progression</th>
                                    <th className="p-4 hidden md:table-cell">Modules complétés</th>
                                    <th className="p-4 hidden lg:table-cell">Quiz</th>
                                    <th className="p-4 hidden lg:table-cell">Réussite</th>
                                    <th className="p-4 text-right">Points</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {users.map((u) => (
                                    <tr
                                        key={u.user_id}
                                        className="hover:bg-white/3 transition-colors"
                                        data-testid={`equipe-user-row-${u.user_id}`}
                                    >
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                {u.avatar ? (
                                                    <img
                                                        src={u.avatar}
                                                        alt=""
                                                        className="w-9 h-9 rounded-xl object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-xl bg-shariti-orange/20 flex items-center justify-center font-bold text-shariti-orange">
                                                        {u.name?.[0]}
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="font-bold text-white truncate">
                                                        {u.name}
                                                    </p>
                                                    <p className="text-xs text-white/40 truncate">
                                                        {u.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 hidden md:table-cell">
                                            <div className="flex items-center gap-2 max-w-[160px]">
                                                <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-shariti-orange rounded-full"
                                                        style={{
                                                            width: `${u.completion_percent}%`,
                                                        }}
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-white">
                                                    {u.completion_percent}%
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4 hidden md:table-cell text-white">
                                            {u.completed_modules}
                                        </td>
                                        <td className="p-4 hidden lg:table-cell text-white/70">
                                            {u.total_attempts}
                                        </td>
                                        <td className="p-4 hidden lg:table-cell">
                                            <span
                                                className={`text-xs font-extrabold px-2 py-1 rounded-full ${
                                                    u.pass_rate >= 80
                                                        ? "bg-shariti-orange/20 text-shariti-orange-light"
                                                        : u.pass_rate >= 50
                                                        ? "bg-shariti-yellow/20 text-shariti-yellow"
                                                        : "bg-white/5 text-white/40"
                                                }`}
                                            >
                                                {u.pass_rate}%
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <p className="text-lg font-extrabold text-shariti-orange leading-none">
                                                {u.points || 0}
                                            </p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>

                {/* Iter-11 — Mastery of associations per recruiter */}
                {mastery && mastery.total_visible_assos_in_org > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                        className="col-span-12 glass-card rounded-3xl overflow-hidden"
                        data-testid="equipe-mastery-section"
                    >
                        <div className="p-5 lg:p-6 border-b border-white/5 flex items-center justify-between gap-3">
                            <div>
                                <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                    Maîtrise terrain
                                </p>
                                <h3 className="text-xl font-extrabold text-white tracking-tight">
                                    Connaissance des associations par recruteur
                                </h3>
                                <p className="text-xs text-white/45 mt-1 max-w-2xl">
                                    Score combiné : 30 % exploration des fiches + 70 % quizzes validés (≥70 %). Clique sur une ligne pour voir le détail association par association.
                                </p>
                            </div>
                            <Heart className="w-6 h-6 text-shariti-orange shrink-0" />
                        </div>
                        <div className="divide-y divide-white/5">
                            {mastery.rows.length === 0 && (
                                <p className="p-6 text-sm text-white/40 italic text-center">
                                    Aucun apprenant actif.
                                </p>
                            )}
                            {mastery.rows.map((r) => {
                                const isOpen = expanded === r.user_id;
                                const tone =
                                    r.mastery_percent >= 70
                                        ? "text-shariti-orange"
                                        : r.mastery_percent >= 35
                                        ? "text-shariti-yellow"
                                        : "text-white/45";
                                const barTone =
                                    r.mastery_percent >= 70
                                        ? "bg-shariti-orange"
                                        : r.mastery_percent >= 35
                                        ? "bg-shariti-yellow"
                                        : "bg-white/15";
                                return (
                                    <div key={r.user_id}>
                                        <button
                                            type="button"
                                            onClick={() => setExpanded(isOpen ? null : r.user_id)}
                                            data-testid={`mastery-row-${r.user_id}`}
                                            className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 hover:bg-white/3 transition-colors text-left"
                                        >
                                            {r.avatar ? (
                                                <img
                                                    src={r.avatar}
                                                    alt=""
                                                    className="w-10 h-10 rounded-xl object-cover shrink-0"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-xl bg-shariti-orange/20 flex items-center justify-center font-extrabold text-shariti-orange shrink-0">
                                                    {r.name?.[0]?.toUpperCase()}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-extrabold text-white truncate">
                                                    {r.name}
                                                </p>
                                                <p className="text-[11px] text-white/40 truncate">
                                                    {r.email}
                                                </p>
                                            </div>
                                            {/* Stats stack — responsive */}
                                            <div className="hidden sm:flex items-center gap-5 text-[11px] shrink-0">
                                                <div className="text-center">
                                                    <p className="text-white/35 font-thin uppercase tracking-widest">
                                                        Visibles
                                                    </p>
                                                    <p className="font-extrabold text-white">
                                                        {r.total_visible}
                                                    </p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-white/35 font-thin uppercase tracking-widest">
                                                        Explorées
                                                    </p>
                                                    <p className="font-extrabold text-white">
                                                        {r.explored}
                                                    </p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-white/35 font-thin uppercase tracking-widest">
                                                        Quiz validés
                                                    </p>
                                                    <p className="font-extrabold text-shariti-orange-light">
                                                        {r.quizzes_passed}
                                                    </p>
                                                </div>
                                                <div className="text-center w-16">
                                                    <p className="text-white/35 font-thin uppercase tracking-widest">
                                                        Score moy.
                                                    </p>
                                                    <p className="font-extrabold text-white">
                                                        {r.avg_quiz_score != null ? `${r.avg_quiz_score}%` : "—"}
                                                    </p>
                                                </div>
                                            </div>
                                            {/* Mastery progress + chevron */}
                                            <div className="flex items-center gap-3 shrink-0 w-44">
                                                <div className="flex-1 min-w-[80px]">
                                                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full ${barTone} rounded-full transition-all`}
                                                            style={{ width: `${r.mastery_percent}%` }}
                                                        />
                                                    </div>
                                                    <p
                                                        className={`text-[10px] font-extrabold uppercase tracking-widest text-right mt-0.5 ${tone}`}
                                                    >
                                                        Maîtrise {r.mastery_percent}%
                                                    </p>
                                                </div>
                                                <ChevronRight
                                                    className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${
                                                        isOpen ? "rotate-90" : ""
                                                    }`}
                                                />
                                            </div>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.18 }}
                                                    className="overflow-hidden bg-white/[0.02]"
                                                >
                                                    <div className="px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                        {r.details.length === 0 && (
                                                            <p className="col-span-full text-xs text-white/40 italic">
                                                                Aucune association attribuée — son manager doit lui ouvrir des accès depuis le backoffice.
                                                            </p>
                                                        )}
                                                        {r.details.map((d) => (
                                                            <div
                                                                key={d.association_id}
                                                                className="bg-white/5 rounded-2xl p-3 flex items-center gap-3"
                                                                data-testid={`mastery-detail-${r.user_id}-${d.association_id}`}
                                                            >
                                                                {d.logo_url ? (
                                                                    <img
                                                                        src={d.logo_url}
                                                                        alt=""
                                                                        className="w-9 h-9 rounded-lg object-contain bg-white/5 p-1 shrink-0"
                                                                    />
                                                                ) : (
                                                                    <div className="w-9 h-9 rounded-lg bg-shariti-orange/15 flex items-center justify-center font-extrabold text-white text-xs shrink-0">
                                                                        {d.name?.[0]}
                                                                    </div>
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-bold text-white truncate">
                                                                        {d.name}
                                                                    </p>
                                                                    <p className="text-[10px] text-white/45 flex items-center gap-1.5 mt-0.5">
                                                                        <Eye className="w-3 h-3" />
                                                                        {d.sections_count}/5 sections
                                                                        {d.video_viewed && " · vidéo vue"}
                                                                    </p>
                                                                </div>
                                                                <div className="text-right shrink-0">
                                                                    {d.has_quiz ? (
                                                                        d.quiz_passed ? (
                                                                            <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-shariti-orange whitespace-nowrap">
                                                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                                                Quiz {d.best_quiz_score}%
                                                                            </span>
                                                                        ) : d.best_quiz_score != null ? (
                                                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-shariti-yellow whitespace-nowrap">
                                                                                <XCircle className="w-3.5 h-3.5" />
                                                                                Quiz {d.best_quiz_score}%
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[10px] text-white/35 italic">
                                                                                Quiz à passer
                                                                            </span>
                                                                        )
                                                                    ) : (
                                                                        <span className="text-[10px] text-white/30 italic">
                                                                            quiz indisponible
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
